import { Modal, TextInput, Button } from "@mantine/core";
import { useForm } from "@mantine/form";
import React, { useState } from "react";
import { Endpoints } from "../../lib/endpoints";
import { showNotification } from "@mantine/notifications";
import { Calendar, CheckCircle, XCircle } from "lucide-react";
import { ISignatureProps } from "./index";

interface ValidateSignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ValidateSignatureModal: React.FC<ValidateSignatureModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureData, setSignatureData] = useState<ISignatureProps | null>(
    null,
  );
  const [isInvalid, setIsInvalid] = useState(false);

  const form = useForm({
    initialValues: {
      signatureId: "",
    },
    validate: {
      signatureId: (value) =>
        value.trim().length === 0 ? "Signature Code is required" : null,
    },
  });

  const handleClose = () => {
    form.reset();
    setSignatureData(null);
    setIsInvalid(false);
    onClose();
  };

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    setSignatureData(null);
    setIsInvalid(false);

    try {
      const token = localStorage.getItem("t");
      const code = values.signatureId.trim().toUpperCase();

      const response = await fetch(Endpoints.GetSignatureById(code).route, {
        method: Endpoints.GetSignatureById(code).method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSignatureData(data.data as ISignatureProps);
        showNotification({
          title: "Valid",
          message: "The signature code is authentic.",
          color: "green",
        });
      } else {
        setIsInvalid(true);
      }
    } catch (error) {
      setIsInvalid(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Validate Signature Code"
      styles={{
        header: { backgroundColor: "#1f2937", color: "white" },
        body: { backgroundColor: "#1f2937", color: "white" },
        title: { fontSize: "1.25rem", fontWeight: 600 },
      }}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)} className="space-y-4">
        <TextInput
          label="Signature Code"
          placeholder="e.g. TA-A1B2C3D4"
          required
          {...form.getInputProps("signatureId")}
          styles={{
            label: { color: "#e5e7eb", marginBottom: "0.25rem" },
            input: {
              backgroundColor: "#111827",
              color: "white",
              borderColor: "#374151",
              textTransform: "uppercase",
            },
          }}
        />

        <div className="flex justify-end pt-2 pb-4 border-b border-gray-800">
          <Button
            type="submit"
            loading={isSubmitting}
            className="w-full bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            Validate
          </Button>
        </div>
      </form>

      {/* Validation Results */}
      {signatureData && (
        <div className="mt-4 p-4 bg-green-900/20 border border-green-500/50 rounded-xl">
          <div className="flex items-center space-x-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <h4 className="text-green-400 font-bold text-lg">
              Authentic Signature
            </h4>
          </div>
          <div className="space-y-2 text-sm text-gray-300">
            <p className="flex flex-col">
              <span className="text-gray-500 text-xs uppercase tracking-wider">
                Issued To
              </span>
              <span className="font-medium text-white text-base">
                {signatureData.issuedTo}
              </span>
            </p>
            <p className="flex flex-col">
              <span className="text-gray-500 text-xs uppercase tracking-wider">
                Date Issued
              </span>
              <span className="font-medium flex items-center">
                <Calendar className="w-3 h-3 mr-1" />
                {new Date(signatureData.createdAt).toLocaleString()}
              </span>
            </p>
            {signatureData.description && (
              <p className="flex flex-col">
                <span className="text-gray-500 text-xs uppercase tracking-wider">
                  Description
                </span>
                <span>{signatureData.description}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {isInvalid && (
        <div className="mt-4 p-4 bg-red-900/20 border border-red-500/50 rounded-xl flex items-center space-x-3">
          <XCircle className="w-6 h-6 text-red-500 shrink-0" />
          <div>
            <h4 className="text-red-400 font-bold">Invalid Signature</h4>
            <p className="text-sm text-gray-400">
              The provided code does not match any valid records in our system.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ValidateSignatureModal;
